import {
  AddAnswerToSessionRequest,
  JsonNode,
  QuestionnaireControllerService,
  StartSessionRequest
} from "@9amhealth/openapi";
import { userState } from "../state";
import QuestionnaireCubit from "./QuestionnaireCubit";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { QuestionnaireType } from "../QuestionnaireStepCubit/QuestionnaireStepCubit";
import { extractErrorCode } from "src/lib/errors";

export class QuestionnaireSession {
  constructor(private owner: QuestionnaireCubit) {}

  /**
   * Session management
   **/
  currentSessionId?: string;
  sessionStarted = false;

  startSession = async (): Promise<{ canProceed: boolean }> => {
    if (userState.isTempUser) return { canProceed: false };
    if (this.sessionStarted) return { canProceed: true };

    this.owner.log("Starting session", this.owner.state);

    const questionnaireId = this.owner.state.formId;
    if (!questionnaireId) return { canProceed: false };
    try {
      const sessionRequest = QuestionnaireControllerService.startSession({
        id: questionnaireId,
        type: StartSessionRequest.type.TYPEFORM
      });

      const session = await sessionRequest;

      document.dispatchEvent(
        new CustomEvent("nineQuestionnaireStarted", {
          bubbles: true,
          composed: true,
          detail: { ...this.owner.state }
        })
      );
      this.sessionStarted = true;

      this.currentSessionId = session.data.sessionId;
      return { canProceed: true };
    } catch (e: unknown) {
      const errorCode = extractErrorCode(e);

      this.sessionStarted = false;
      this.owner.patch({
        error: `session_${this.owner.state.formId}_${errorCode}`
      });
      reportErrorSentry(e);
      return { canProceed: false };
    }
  };

  addAnswerToSession = async (questionId: string) => {
    if (!this.sessionStarted) {
      throw new Error("Session not started");
    }
    if (!this.currentSessionId) {
      addSentryBreadcrumb(
        "api",
        `Session not started when adding answer, creating new one`
      );
      this.owner.log(
        "Session not started when adding answer, creating new one"
      );
      const { canProceed } = await this.startSession();

      if (!canProceed) {
        throw new Error("Session could not be started");
      }
    }

    const typeNoAnswer = [
      QuestionnaireType.STATEMENT,
      QuestionnaireType.THANK_YOU
    ];
    const field = this.owner.getFieldByRef(questionId);
    const fullParsedAnswer = this.owner.parseAnswer(questionId);

    const sendAnswer = field ? !typeNoAnswer.includes(field.type) : false;

    if (!sendAnswer) {
      return;
    }

    try {
      if (!this.currentSessionId) {
        throw new Error("Session not started");
      }
      this.owner.log("Adding answer to session", questionId);

      const answer = {
        questionId,
        answer: (fullParsedAnswer?.fieldValue ?? "") as unknown as JsonNode
      } satisfies AddAnswerToSessionRequest;

      await QuestionnaireControllerService.addAnswerToSession(
        this.currentSessionId,
        answer
      );
    } catch (e) {
      reportErrorSentry(e);
    }
  };
}
